// App root. Boots by resolving identity through the catalog fetch (GET
// /api/bi/dashboards doubles as the boot probe, there is no /api/bi/me route,
// see lib/api.ts): 401 -> sign-in panel, 403 -> "not authorized for BI" panel,
// ok -> the routed app. The router only mounts once signed in. `bi_site` at
// `read` reaches every page, so there is no second permissions fetch here.
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Button, ToastProvider, TooltipProvider } from "@bi/shared";

import { SignInPanel } from "./components/sign-in-panel";
import { EmptyState, ErrorState, LoadingState } from "./components/states";
import { ApiError } from "./lib/api";
import { activePreviewCatalog, leavePreview, previewCatalogLabel } from "./lib/preview-catalog";
import { dashboardsQuery } from "./lib/queries";
import { queryClient } from "./lib/query-client";
import { router } from "./router";

function Boot() {
  const dashboards = useQuery(dashboardsQuery);
  const preview = activePreviewCatalog();

  if (dashboards.isPending) {
    return (
      <div className="bi-boot">
        <LoadingState label="Signing in…" />
      </div>
    );
  }
  if (dashboards.isError) {
    if (dashboards.error instanceof ApiError) {
      if (dashboards.error.status === 401) return <SignInPanel />;
      if (dashboards.error.status === 403) return <SignInPanel forbidden />;
      // A preview that is gone: most likely its pull request was merged or
      // closed, which prunes the preview on purpose. Say so plainly instead of
      // the generic "Request failed (404)".
      // ONLY AT BOOT (`!dashboards.data`): `?catalog=` is retained on every
      // route, so a focus refetch that 404s after the merge must not replace
      // the RouterProvider and lose an unsaved studio canvas.
      if (dashboards.error.status === 404 && preview !== null && !dashboards.data) {
        return (
          <div className="bi-boot">
            <div className="bi-boot__stack">
              <EmptyState
                title={`No preview for pull request ${previewCatalogLabel(preview)}`}
                message="It was merged or closed, or its specs have not been published yet."
              />
            {/* A way out: `?catalog=` is retained on every route, so a stale
                preview bookmark 404s here on every page. Without this button
                the only escape is editing the URL. */}
              <Button variant="solid" tone="neutral" onClick={() => leavePreview()}>
                Leave preview
              </Button>
            </div>
          </div>
        );
      }
    }
    return (
      <div className="bi-boot">
        <ErrorState error={dashboards.error} onRetry={() => void dashboards.refetch()} />
      </div>
    );
  }

  return <RouterProvider router={router} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        {/* Shares hover/focus timing across every `Tooltip` (chart-card.tsx's
         *  `(i)` needs it). `delay={0}` on purpose: the `(i)` is a deliberate
         *  target and the charts beside it answer hover instantly, so a delay
         *  reads as a broken tooltip. */}
        <TooltipProvider delay={0}>
          <Boot />
        </TooltipProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
